<template>
  <modal
    v-if="show"
    width="560px"
    bodyOverflow="auto"
    :title="form.id ? t('Edit Workspace') : t('Create Workspace')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/workspaces`"
  >
    <div class="w100 overflow-auto campaign-details__wrap">
      <ui-input
        v-model="form.name"
        :label="t('Name')"
        :placeholder="t('Private Workspace Name')"
        :disabled="form.name === 'Master' || form.name === 'Public'"
        :error-messages="errorsDict['name']"
        @change="clearError('name')"
      />

      <ui-select
        v-model="formTrackingDomains"
        class="domains-select"
        :placeholder="t('Account default')"
        :label="t('Tracking Domains')"
        :label-help="t('Tracking domain dedicated to the specific workspace')"
        :items="store.trackingDomains.data"
        hide-selected
        item-text="domain"
        item-value="id"
        multiple
        combobox
        multi-line
        chips
        :return-object="true"
        :error-messages="errorsDict['trackingDomainIds']"
      />
    </div>

    <template #footer>
      <section class="d-flex w100 justify-space-between align-center xs12 error-wrapper">
        <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        <div class="d-flex justify-end align-items-center">
          <ui-update-info infoFormat="short" v-if="form.id" :data="workspace" />
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="checkClose"
          />

          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="save"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import _ from 'lodash';
import md5 from 'md5';
import {
  postUserWorkspaceApiRequest,
  putUserWorkspaceApiRequest,
} from '@/services/api/workspaces-api';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import { useApiFieldErrors, useI18n } from '@/composables';
import type { UserWorkspace } from '@/services/models';
import { useStore } from '@/store';
import $emitter from '@/services/emitter';

type WorkspaceForm = {
  id?: string;
  name: string;
  trackingDomains: string[];
  type?: string;
};

const props = defineProps<{
  data?: {
    workspace?: Record<string, unknown>;
    setActiveWorkspace?: (id: string) => void;
    applyWorkspace?: (workspace: UserWorkspace) => void;
  };
}>();

const emit = defineEmits<{ close: [] }>();

const { t, locale } = useI18n();
const store = useStore();
const {
  fieldErrors,
  errorsDict,
  errors,
  errorsControlActive,
  clearErrors,
  applyApiErrors,
} = useApiFieldErrors();

const show = ref(false);
const loading = ref(false);
const workspace = ref<Record<string, unknown>>({});
const form = reactive<WorkspaceForm>({
  name: '',
  trackingDomains: [],
});
const oldHash = ref('');

const getCurrentHash = () => md5(JSON.stringify(form));

const clearError = (field: string) => {
  const newValue = { ...fieldErrors.value };
  delete newValue[field];
  fieldErrors.value = newValue;
};

const formTrackingDomains = computed({
  get: () => {
    const trackingDomains: string[] = form.trackingDomains || [];
    return trackingDomains.map(id => store.trackingDomainsObject[id]);
  },
  set: (value: { id: string }[]) => {
    form.trackingDomains = value.filter(item => item && item.id).map(item => item.id);
  },
});

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => close() },
    });
    return;
  }
  close();
};

const close = () => {
  emit('close');
};

const save = async () => {
  clearErrors();
  loading.value = true;
  store.setLoading(true);

  let formToSend: Record<string, unknown> = {
    ...workspace.value,
    name: form.name.trim(),
    trackingDomainIds: form.trackingDomains,
  };

  if (formToSend.type === 'public') {
    formToSend = _.omit(formToSend, 'memberIds');
  }

  try {
    const data = form.id
      ? await putUserWorkspaceApiRequest({
          ...formToSend,
          id: form.id,
        })
      : await postUserWorkspaceApiRequest(formToSend);

    props.data?.applyWorkspace?.(data);
    await refreshUserProfileFromApi();

    close();
    const message = form.id
      ? t('Workspace successfully updated')
      : t('Workspace successfully added');

    if (!form.id) {
      props.data?.setActiveWorkspace(data.id);
    }

    $emitter.emit('toast-success', message);
  } catch (error: unknown) {
    applyApiErrors(error);
    return Promise.reject(error);
  } finally {
    loading.value = false;
    store.setLoading(false);
  }
};

onMounted(async () => {
  if (!store.trackingDomains.isUploaded) {
    await populateTrackingDomainsStoreCacheApiRequest();
  }

  if (props.data?.workspace) {
    workspace.value = props.data.workspace;
    const { trackingDomainIds = [] } = props.data.workspace;
    const trackingDomains: string[] = [];
    (trackingDomainIds as string[]).forEach(id => {
      if (store.trackingDomainsObject[id]) {
        trackingDomains.push(store.trackingDomainsObject[id].id);
      }
    });
    Object.assign(form, {
      ..._.omit(props.data.workspace, 'trackingDomainIds'),
      trackingDomains,
    });
  } else {
    Object.assign(form, {
      name: '',
      trackingDomains: [],
    });
  }

  oldHash.value = getCurrentHash();
  show.value = true;
});
</script>

<style lang="scss" scoped>
.error-wrapper {
  width: 100%;
}

:deep(.v-select__selections) {
  padding-top: 4px;
  padding-bottom: 4px;
  box-sizing: border-box;
  input {
    height: 25px !important;
  }
}

.error--text.domains-select {
  :deep(label) {
    color: var(--color-black) !important;
    margin-left: 0 !important;
  }
  :deep(.v-field__input .v-select__slot .v-select__selections) {
    border: 1px solid var(--color-red) !important;
  }
  :deep(.v-input__control .v-field__input .v-select__slot .v-select__selections input) {
    border: none !important;
  }
}

.domains-select__selected {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 15px;
  margin-right: 8px;
  border-radius: 4px;
  background: var(--color-gray5);
  display: flex;
  align-items: baseline;
}
</style>
